import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AddressData, UtilityService } from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService, IconContext, IconTextWidget } from '@eforall/dashboard';
import { Venue, VenueZip } from '@interfaces';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { VenueAddPart } from './add/venue-add.part';
import { VenueChooseVenuePart } from './choose-venue-part/choose-venue.part';
import { VenueDialogDataService } from './venue-dialog-data.service';

@Component({
	selector: 'venue-dialog',
	imports: [
		DashboardDialogFramePart,
		TabStripModule,
		FormsModule,
		IconTextWidget,
		VenueChooseVenuePart,
		VenueAddPart,
	],
	templateUrl: './venue.dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./venue.dialog.scss'],
})
export class VenueDialog implements OnInit {
	public dialog: DialogContext<{ venueId: number | undefined }> = inject(MAT_DIALOG_DATA);
	public dialogRef = inject(MatDialogRef<VenueDialog>);
	private venueDialogDataService = inject(VenueDialogDataService);
	public util = inject(UtilityService);
	public dialogService = inject(DialogService);

	public zip = signal<VenueZip | undefined>(undefined);
	public venue = signal<Venue | undefined>(undefined);

	public addIconContext = signal<IconContext>({
		iconClass: 'fa-plus',
		iconStyle: 'fa-regular',
		text: 'Add',
	});

	public invalidZip = false;
	public adding = signal(false);

	closeAction: DialogAction = {
		id: 'close',
		enabled: signal(true),
		cssClass: signal(undefined),
		visible: signal(true),
		text: signal('Close'),
		linkType: 'callback',
		callback: async () => this.close(),
		willCloseDialog: true,
	};

	selectActionEnabled = signal(false);

	selectAction: DialogAction<Venue> = {
		id: 'select',
		enabled: this.selectActionEnabled,
		visible: signal(true),
		text: signal('Select'),
		cssClass: signal('btn-green'),
		linkType: "callback",
		callback: async () => this.venue()!,
		willCloseDialog: true,
	};

	actions = [this.selectAction, this.closeAction];

	async ngOnInit() {
		const venueId = this.dialog.data?.venueId;
		await this.venueDialogDataService.loadAllVenueData();

		if (venueId) {
			const venue = this.venueDialogDataService.venues().find(v => v.venueId === venueId);
			this.venue.set(venue);
			if (venue) {
				this.zip.set(this.venueDialogDataService.zips().find(z => z.zipId === venue.zipId));
			}
		}

	}

	async onZipInput(value: string) {

		this.invalidZip = false;

		const zipId = parseInt(value || '0', 10);
		if (!zipId || isNaN(zipId)) {
			this.zip.set(undefined);
			return;
		}

		if (zipId && value.length == 5) {
			const zip = this.venueDialogDataService.zips().find(z => z.zipId === zipId);
			if (zip) {
				this.zip.set(zip);
			} else {
				this.zip.set(undefined);
				this.invalidZip = true;
			}
		}

	}

	onVenueSelection(venue: Venue | undefined) {
		this.selectActionEnabled.set(!!venue);
		if (this.venue()?.venueId == venue?.venueId) return;
		this.venue.set(venue);
	}

	close() {
		this.dialogRef.close();
	}


	public setAdding() {
		this.adding.set(true);
		this.selectActionEnabled.set(false);
	}


	public async addAddress(address: AddressData | undefined) {
		console.log('Found place:', address);
		if (!address) return;

		this.venue.set(undefined);

		const venueId = await this.venueDialogDataService.addVenue({
			latitude: address.latitude!,
			longitude: address.longitude!,
			placeAddress: address.placeAddress,
			googlePlaceKey: address.googlePlaceKey!,
			streetAddress: address.streetAddress!,
			zipId: address.zipId!,
			officialName: address.displayName ?? address.streetAddress!,
			website: address.websiteURI,
		});


		this.adding.set(false);

		const venue = this.venueDialogDataService.venues().find(v => v.venueId === venueId);
		this.venue.set(venue);
		this.zip.set(this.venueDialogDataService.zips().find(z => z.zipId === venue?.zipId));

	}
}
